import type { PropsWithChildren, ReactElement } from "react";

import { ActionButton, ActionButtonIntent, type ActionButtonProps, SymbolIcon } from "widgets";

import type { PressEvent } from "react-aria";
import type { IPlaybookPage } from "../../app-utils/playbook.js";
import PlaybookScreenSection from "./PlaybookScreenSection.js";

export interface IWizardFooterData {
  error?: string;
  warning?: string;
  nextButtonProps?: ActionButtonProps;
  enableSubmissionUponWizardError?: boolean;
}

export interface IPlaybookWizardStepProvidedProps {
  playbookPage: Partial<IPlaybookPage>;
  wizardFooterData?: IWizardFooterData;
  tools?: ReactElement;
}

export interface IPlaybookWizardStepInjectedProps {
  stepIndex: number;
  backToPreviousStep: (event: PressEvent, forceStep?: number) => void;
  proceedWithNextStep: () => void;
  closeWizard: () => void;
  wizardErrorMessage: string;
  updateWizardMessage?: (error: string | null) => void;
  updateConfigurationError?: (error: any) => void; // configuration error is stored in the wizard parent component
}

export type IWizardStep = ReactElement<IPlaybookWizardStepInjectedProps>;

export interface IPlaybookWizardStepProps extends IPlaybookWizardStepProvidedProps, IPlaybookWizardStepInjectedProps {}

function PlaybookWizardStep(props: PropsWithChildren<IPlaybookWizardStepProps>) {
  const errorMessage: string | undefined = props.wizardErrorMessage || props.wizardFooterData?.error;

  return (
    <div className={"playbook--step"} id={"scrollContainerElement"}>
      {props.playbookPage.title && (
        <div className={"playbook-step-header"}>
          {props.stepIndex}. {props.playbookPage.title}
          {props.tools}
        </div>
      )}

      <div className={"playbook-step-body"}>
        <PlaybookScreenSection title={props.playbookPage.label} description={props.playbookPage.description} />
        <div className={"playbook-step-custom"}>{props.children}</div>
      </div>

      <div className={"playbook-step-shadow"} />

      <div className={"playbook-step-footer"}>
        {errorMessage && (
          <div className="wizard-step-message error">
            <SymbolIcon icon={"warning"} />
            <div className="message">{errorMessage}</div>
          </div>
        )}

        {/*If we already showed an error, there's no point in showing a warning*/}
        {!errorMessage && props.wizardFooterData?.warning && (
          <div className="wizard-step-message warning">
            <div className="message">{props.wizardFooterData.warning}</div>
          </div>
        )}

        <div className={"footer-buttons"} style={{ display: "flex", columnGap: 5 }}>
          {props.stepIndex > 1 && (
            <ActionButton
              label={"Go Back"}
              id="previousButton"
              onPress={props.backToPreviousStep}
              intent={ActionButtonIntent.Secondary}
            />
          )}

          {/* do NOT disable in case of error, it is of the responsibility of the screen to provide the nextButtonProps.disabled (otherwise no way to re-submit sometimes) */}
          <ActionButton
            label={"Next"}
            onPress={props.proceedWithNextStep}
            className={"next-step-button"}
            {...props.wizardFooterData?.nextButtonProps}
            isDisabled={
              props.wizardFooterData?.nextButtonProps?.isDisabled ||
              (!!props.wizardErrorMessage && !props.wizardFooterData?.enableSubmissionUponWizardError)
            }
          />
        </div>
      </div>
    </div>
  );
}

export default PlaybookWizardStep;
